import React, { useCallback, useState } from 'react';
import { FaCommentAlt } from 'react-icons/fa';
import { HiOutlineEye } from 'react-icons/hi2';
import { IoTicketSharp } from 'react-icons/io5';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { ActionIcon, Button, Input, Switch, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import FileUpload from '../../../components/FileUpload/FileUpload';
import ModalForm from '../../../components/forms/ModalForm';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { ClientService } from '../../../services/client/clientService';
import { ClientsApiResponse } from '../../../type/entities/client-type';
import ClientsFilters from './ClientsFilters';
import { AddCommentModal } from './components/AddCommentModal';
import CallModalForm from '../../contracts/components/CallModalForm';
import { BiSolidPhoneCall } from "react-icons/bi";
import {
    CREDIT_DOCUMENTS_UPLOAD_TITLE,
    validateCreditDocumentsRequired,
} from '../utils/creditDocumentsValidation';

interface ClientsListProps { }
interface CreditPreviewContract {
    id: string;
    code: string;
}

interface CreditIncidencePreview {
    incidencesToGenerate: number;
    contracts: CreditPreviewContract[];
}

const columnOrder = [
    'id',
    'companyName',
    'cifNif',
    'cnae',
    'email',
    'iban',
    'currentCredits',
    'telephone',
    'telephoneSecondary',
    'mobile',
    'personResponsibleName',
    'personResponsibleNif',
    'lastComment',
    'active',
    'createdAt',
    'updatedAt',
];

const ClientsList: React.FC<ClientsListProps> = ({ }) => {

    const { userCan, isFacilitatorManager } = usePrivilege();
    const canViewContracts = userCan('get_contracts', 'contracts');
    const canEditClients = userCan('edit_clients', 'clients') && !isFacilitatorManager;
    const canDeleteClients = userCan('delete_clients', 'clients') && !isFacilitatorManager;
    const canAddCredits = userCan('add_client_credits', 'clients') && !isFacilitatorManager;
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new ClientService();

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [openCommentModal, setOpenCommentModal] = useState<boolean>(false);
    const [rowId, setRowId] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [phoneSecondary, setPhoneSecondary] = useState<string>('');
    const [mobile, setMobile] = useState<string>('');
    const [openCallModal, setOpenCallModal] = useState<boolean>(false);
    const [openCreditModal, setOpenCreditModal] = useState<boolean>(false);
    const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
    const [credits, setCredits] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [sendIncidenceEmails, setSendIncidenceEmails] = useState<boolean>(false);
    const [creditIncidencePreview, setCreditIncidencePreview] = useState<CreditIncidencePreview | null>(null);
    const [creditUpdateFiles, setCreditUpdateFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listClients(filters);
        return response.getResponseData() as ClientsApiResponse;
    }, [filters]));

    const toggleState = async (id: string, status: boolean) => {
        if (!canEditClients) {
            toast.error('No tienes permisos para editar clientes');
            return;
        }
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleClientState(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el cliente" : "Se ha activado el cliente");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };

    const getDefaultCreditDateRange = () => {
        const currentYear = new Date().getFullYear();
        return {
            startDate: `${currentYear}-01-01`,
            endDate: `${currentYear}-12-31`,
        };
    };

    const resetCreditForm = () => {
        setCredits('');
        setStartDate('');
        setEndDate('');
        setSendIncidenceEmails(false);
        setCreditIncidencePreview(null);
        setCreditUpdateFiles([]);
    };

    const closeCreditModal = () => {
        setOpenCreditModal(false);
        resetCreditForm();
    };

    const closeConfirmModal = (reopenCreditModal: boolean = false) => {
        setOpenConfirmModal(false);
        setCreditIncidencePreview(null);
        setSendIncidenceEmails(false);
        if (reopenCreditModal) {
            setOpenCreditModal(true);
        }
    };

    const openCreditModalForClient = (clientId: string) => {
        const defaults = getDefaultCreditDateRange();
        setRowId(clientId);
        setCredits('');
        setStartDate(defaults.startDate);
        setEndDate(defaults.endDate);
        setSendIncidenceEmails(false);
        setCreditIncidencePreview(null);
        setCreditUpdateFiles([]);
        setOpenCreditModal(true);
    };

    const submitCreditsUpdate = async (parsedCredits: number, sendIncidenceEmailsValue: boolean) => {
        const response = await (await service.addCredits(
            rowId,
            parsedCredits,
            startDate,
            endDate,
            sendIncidenceEmailsValue,
            undefined,
            undefined,
            creditUpdateFiles
        )).getResponseData();
        if (response.success) {
            setOpenConfirmModal(false);
            closeCreditModal();
            toast.success('Créditos actualizados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const addCredits = async () => {
        const parsedCredits = parseFloat(credits);
        if (credits.trim() === '' || Number.isNaN(parsedCredits)) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }

        if (parsedCredits < 0) {
            toast.error('La cantidad de créditos no puede ser negativa');
            return;
        }

        if (!validateCreditDocumentsRequired(creditUpdateFiles)) {
            return;
        }

        setIsLoading(true);
        const previewResponse = await (await service.previewAddCredits(rowId, parsedCredits, startDate, endDate)).getResponseData();
        if (!previewResponse.success) {
            handleErrors(previewResponse);
            setIsLoading(false);
            return;
        }

        const previewData = previewResponse.data || {};
        if (previewData.willGenerateIncidences) {
            const contracts: CreditPreviewContract[] = Array.isArray(previewData.contracts)
                ? previewData.contracts
                    .filter((contract: any) => contract && typeof contract.code === 'string' && contract.code.trim() !== '')
                    .map((contract: any) => ({
                        id: typeof contract.id === 'string' ? contract.id : '',
                        code: contract.code,
                    }))
                : [];
            setCreditIncidencePreview({
                incidencesToGenerate: previewData.incidencesToGenerate || 0,
                contracts,
            });
            setOpenCreditModal(false);
            setOpenConfirmModal(true);
            setIsLoading(false);
            return;
        }

        await submitCreditsUpdate(parsedCredits, false);
        setIsLoading(false);
    };

    const confirmCreditsUpdate = async () => {
        const parsedCredits = parseFloat(credits);
        if (credits.trim() === '' || Number.isNaN(parsedCredits) || parsedCredits < 0) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }

        if (!validateCreditDocumentsRequired(creditUpdateFiles)) {
            return;
        }

        setIsLoading(true);
        await submitCreditsUpdate(parsedCredits, sendIncidenceEmails);
        setIsLoading(false);
    };

    const handleDelete = async (id: string) => {
        const response = await (await service.deleteClient(id)).getResponseData();
        if (response.success) {
            toast.success('Cliente eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiClients(ids)).getResponseData();
        if (response.success) {
            toast.success('Clientes eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    largeTable
                    id={'clients-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={[
                        'telephoneSecondary',
                        'mobile'
                    ]}
                    overrideColumns={[
                        {
                            key: 'companyName',
                            render: (row: any) => {
                                return (
                                    userCan('get_clients', 'clients')
                                        ? (
                                            <Link to={`${menuRoutes.clients.path}/${row.row.original.id}/profile${menuRoutes.clients.profile.info}`}>
                                                <Text className='font-bold text-primary'>{row.row.original.companyName}</Text>
                                            </Link>
                                        )
                                        : <Text className='font-bold text-primary'>{row.row.original.companyName}</Text>
                                )
                            },
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                if (!canEditClients) {
                                    return row.row.original.active ? 'Activo' : 'Desactivado';
                                }
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.active ? 'Desactivar cliente' : 'Activar cliente'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.active}
                                            onChange={() => toggleState(row.row.original.id, row.row.original.active)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Realizar llamada',
                            permissions: { group: 'calls', permission: 'realize_calls' },
                            render: (row: any) => {
                                const hasPhone = Boolean(row.telephone && row.telephone.trim() !== "");
                                const hasPhoneSecondary = Boolean(row.telephoneSecondary && row.telephoneSecondary.trim() !== "");
                                const hasMobile = Boolean(row.mobile && row.mobile.trim() !== "");
                                let hasAnyPhone = true;

                                if (!hasPhone && !hasPhoneSecondary && !hasMobile) {
                                    hasAnyPhone = false;
                                }

                                return (
                                    <Tooltip
                                        size="sm"
                                        content={
                                            hasAnyPhone
                                                ? 'Realizar llamada'
                                                : 'El cliente no posee ningún número de teléfono'
                                        }
                                        placement="top"
                                        color={hasAnyPhone ? "invert" : "danger"}
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className={
                                                hasAnyPhone
                                                    ? "hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                    : "opacity-50 cursor-not-allowed"
                                            }
                                            disabled={!hasAnyPhone}
                                            onClick={() => {
                                                if (!hasAnyPhone) return;
                                                setRowId(row.id);
                                                setPhone(row.telephone);
                                                setPhoneSecondary(row.telephoneSecondary);
                                                setMobile(row.mobile);
                                                setOpenCallModal(true);
                                            }}
                                        >
                                            <BiSolidPhoneCall className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: 'Add Credits',
                            render: (row: any) => {
                                if (!canAddCredits) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Añadir créditos'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => openCreditModalForClient(row.id)}
                                        >
                                            <IoTicketSharp className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Add Comment',
                            permissions: { group: 'clients', permission: 'create_client_comments' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Añadir comentario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => { setRowId(row.id); setOpenCommentModal(true) }}
                                        >
                                            <FaCommentAlt className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'View',
                            permissions: { group: 'clients', permission: 'get_clients' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.clients.path}/${row.id}/profile${menuRoutes.clients.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            render: (row: any) => {
                                if (!canEditClients) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar cliente'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.clients.path}/${row.id}/edit`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            render: (row: any) => {
                                if (!canDeleteClients) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar cliente`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.companyName}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={canDeleteClients ? handleMultiDelete : undefined}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ClientsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <CallModalForm isOpen={openCallModal} onClose={() => setOpenCallModal(false)} refetch={refetch} currentClientId={rowId} phone={phone} phoneSecondary={phoneSecondary} mobile={mobile} />

            <AddCommentModal service={(new ClientService).addComment} id={rowId} isOpen={openCommentModal} onClose={() => setOpenCommentModal(false)} refetch={refetch} />

            <ModalForm isOpen={openCreditModal} onClose={closeCreditModal} title='Añadir actualización de créditos'>
                <Input
                    label='Créditos'
                    type='number'
                    value={credits}
                    onChange={(e) => setCredits(e.target.value)}
                />

                <Input
                    label='Fecha de inicio de validez de los créditos'
                    type='date'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />

                <Input
                    label='Fecha de fin de validez de los créditos'
                    type='date'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />

                <div className='pt-4'>
                    <FileUpload
                        files={creditUpdateFiles}
                        onFilesChange={setCreditUpdateFiles}
                        title={CREDIT_DOCUMENTS_UPLOAD_TITLE}
                        dropText='Arrastra archivos aquí o haz clic para seleccionarlos'
                        dragActiveText='Suelta los archivos aquí...'
                        maxFiles={50}
                        maxSize={52428800}
                        showFileList
                        requiredAsterisk={true}
                    />
                </div>

                <div className='flex justify-center p-7'>
                    <Button
                        disabled={isLoading || !startDate || !endDate || credits.trim() === '' || creditUpdateFiles.length === 0}
                        onClick={() => addCredits()}
                        color='secondary'
                    >
                        Actualizar créditos
                    </Button>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={openConfirmModal}
                onClose={() => closeConfirmModal(true)}
                title='Confirmar actualización de créditos'
            >
                <div className='space-y-4 pb-6'>
                    <p className='text-sm text-gray-700'>
                        Esta actualización generará {creditIncidencePreview?.incidencesToGenerate || 0} incidencia(s). ¿Deseas continuar?
                    </p>

                    {creditIncidencePreview?.contracts && creditIncidencePreview.contracts.length > 0 && (
                        <div>
                            <p className='text-sm font-medium mb-2'>Contratos vinculados</p>
                            <div className='flex flex-wrap gap-2'>
                                {creditIncidencePreview.contracts.map((contract, index) => (
                                    canViewContracts && contract.id
                                        ? (
                                            <Link
                                                key={contract.id}
                                                to={`${menuRoutes.contracts.path}/${contract.id}/profile${menuRoutes.contracts.profile.info}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-primary hover:bg-gray-50'
                                            >
                                                {contract.code}
                                            </Link>
                                        )
                                        : (
                                            <span
                                                key={`${contract.id || 'contract'}-${contract.code}-${index}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-gray-700'
                                            >
                                                {contract.code}
                                            </span>
                                        )
                                ))}
                            </div>
                        </div>
                    )}

                    <div className='pt-2'>
                        <Switch
                            label='Enviar correo a responsables/participantes si se registran incidencias'
                            checked={sendIncidenceEmails}
                            onChange={() => setSendIncidenceEmails(!sendIncidenceEmails)}
                            switchKnobClassName='bg-secondary'
                            switchClassName='w-[65px]'
                        />
                    </div>

                    <div className='flex justify-end gap-3 pt-2'>
                        <Button variant='outline' onClick={() => closeConfirmModal(true)} disabled={isLoading}>
                            Cancelar
                        </Button>
                        <Button color='secondary' onClick={confirmCreditsUpdate} isLoading={isLoading} disabled={isLoading}>
                            Confirmar y actualizar
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Clientes'}>
                <ClientsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default ClientsList;
